'use client';

import { Box, Text, HStack, Image, Spinner } from '@chakra-ui/react';
import { Copy } from '@/components/ui/svgs/TradePage/TokenDetail';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useState, useMemo, useRef } from 'react';
import { AgentData, useAINarrative } from '@/lib/services/agent.service';
import {
  marketCapValueFormat,
  formatPrice,
  formatNumber,
} from '@/lib/utils/number.utils';
import {
  fetchCreateTradeMarketOrder,
  TradeMarketOrderParams,
} from '@/lib/services/trade.service';
import { toaster } from '@/components/ui/toaster';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/navigation';
import { usePathname } from 'next/navigation';
import { reduxFundToken } from '@/lib/stores/walletSlice';
import { reduxCandleStickTickData } from '@/lib/stores/dataSlice';
import { reduxWebsocketState } from '@/lib/stores/stateSlice';
import { clearWsSimpleToken } from '@/lib/stores/wsSimpleTokenInfo';
import { useAppDispatch, useAppSelector } from '@/lib/hooks/storeHook';
import {
  setSubscribe,
  reduxTokenInfoSubscribe,
  SubscribeKeyType,
} from '@/lib/stores/subscribeSlice';
import {
  selectWsSimpleToken,
  selectWsSimpleTokens,
} from '@/lib/stores/wsSimpleTokenInfo';
import { abbreviateHashString } from '@/lib/utils/number.utils';
import { Button } from '@/components/ui/button';
import { useLoginRedirect } from '@/lib/hooks/useLoginRedirect';
import nProgress from 'nprogress';
import { WebsocketAction } from '@/lib/hooks/useSocketData';
import { useInView } from 'react-intersection-observer';
import { motion, useAnimation } from 'motion/react';
import Decimal from 'decimal.js';
import {
  CHAIN_ID_TO_LOWER_NAME,
  CHAIN_ID_MAP,
  CHAIN_INFO,
} from '@/lib/config/chain';
import { TokenAvatar } from '../common/TokenAvatar';
import { useChainInfo } from '@/lib/hooks/useChainInfo';

interface AgentCardProps {
  card: any;
  colorScheme: string;
}

interface TokenInfo {
  address: string;
  ath_mkt_cap: number;
  chain_id: number;
  mkt_cap: number;
  mkt_return: number;
  symbol: string;
  token_icon: string;
  token_price?: number;
  vol_24h?: number;
}

type qucikBuyProps = {
  name: string;
  value: string;
};

type NewItemProps = {
  message: AgentData;
  quickBuyInfo: qucikBuyProps[];
};

type quickBuyInfoProps = {
  name: string;
  value: string;
};

export default function AgentItem({ message, quickBuyInfo }: NewItemProps) {
  const { isLogin, openLoginModal } = useLoginRedirect();
  const dispatch = useAppDispatch();
  const wsSimpleToken = useSelector(selectWsSimpleToken);
  const rWebsocketState = useAppSelector(reduxWebsocketState);
  const fundToken = useSelector(reduxFundToken) || {};
  const t = useTranslations('HomePage');
  const e = useTranslations('TradePage.TokenDetail');
  const maxReturnMap = useRef(new Map<string, number>());
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();
  const [isLoading, setIsLoading] = useState(false);
  const [num, setNum] = useState('');
  const rTokenInfoSubscribe = useAppSelector(reduxTokenInfoSubscribe);
  const [narrativeExpanded, setNarrativeExpanded] = useState<boolean>(false);
  const [fetchedNarrative, setFetchedNarrative] = useState<string>('');
  const { chainInfo } = useChainInfo();
  const { ref, inView, entry } = useInView({
    threshold: 0.1,
    triggerOnce: false, // 设置为true只触发一次
  });

  const [fadeOutColor, setFadeOutColor] = useState<string>(
    'rgba(254, 91, 121, 0)',
  );

  const controls = useAnimation();

  const { data: narrativeData } = useAINarrative(
    message.address || '',
    !message.aiNarrative && !fetchedNarrative,
  );

  const narrative = useMemo(() => {
    return message.aiNarrative || narrativeData?.message;
  }, [narrativeData, message.aiNarrative]);

  useEffect(() => {
    if (narrativeData?.status === '2') {
      setFetchedNarrative(narrativeData?.message);
    }
  }, [narrativeData]);

  const tokenInfo = useMemo(() => {
    try {
      return JSON.parse(message.tokenInfo ?? '{}') as TokenInfo;
    } catch (error) {
      return {} as TokenInfo;
    }
  }, [message.tokenInfo]);

  const chainId = message?.chainId;
  const address = message?.address;

  useEffect(() => {
    if (rWebsocketState.isConnected && inView) {
      rWebsocketState.sendJsonMessage({
        action: WebsocketAction.SUBSCRIBE,
        channel: 'simple_token_info',
        data: {
          chain: Number(chainId),
          address: address,
        },
      });
    }

    return () => {
      if (rWebsocketState.isConnected && inView) {
        rWebsocketState.sendJsonMessage({
          action: WebsocketAction.UNSUBSCRIBE,
          channel: 'simple_token_info',
          data: {
            chain: Number(chainId),
            address: address,
          },
        });
      }
      // dispatch(clearWsSimpleToken());
    };
  }, [chainId, address, rWebsocketState, inView]);

  const onQuickBuy = (key: string, type: string) => {
    if (!isLogin) {
      openLoginModal();
      return;
    }
    let flag = true;
    if (type == 'sell') {
      let address = message.address;
      let amount = '0';
      fundToken.tokenAssets.forEach((item) => {
        if (address == item.tokenCa) {
          amount = item.tokenAmount;
        }
      });
      if (Number(amount) <= 0) {
        flag = false;
      }
    }
    if (flag) {
      let amount = '';
      type == 'buy' ? (amount = key) : '';
      setNum(key);
      setIsLoading(true);
      let params: TradeMarketOrderParams = {
        chain_id: Number(message?.chainId),
        token_ca: message?.address || '',
        amount_in: key,
        swap_type: type == 'buy' ? 1 : 2,
        is_one_click: false,
        double_out: false,
      };
      fetchCreateTradeMarketOrder(params)
        .then((res) => {
          setIsLoading(false);
          setNum('');
        })
        .catch((err) => {
          setIsLoading(false);
          setNum('');
        });
    } else {
      toaster.create({
        title: `${t('agent.noBalance')}`,
        type: 'error',
      });
    }
  };

  const renderQuickOneClickBuy = (list: quickBuyInfoProps[], type: string) => {
    return (
      <Box
        w={'100%'}
        display={'grid'}
        gridTemplateColumns={'repeat(3, 1fr)'}
        gap={'8px 8px'}
      >
        {list.map((item: qucikBuyProps) => (
          <HStack
            flex={1}
            justifyContent={'center'}
            alignItems={'center'}
            bg={'rgba(1, 188, 141, 0.1)'}
            h={'32px'}
            borderRadius={'6px'}
            cursor={'pointer'}
            onClick={() => {
              onQuickBuy(item.value, type);
            }}
          >
            <Button
              color={type == 'buy' ? '#01BC8D' : '#FE5B79'}
              bg={'rgba(1, 188, 141, 0.1)'}
              fontSize={'12px'}
              w={'100%'}
              h={'32px'}
              loading={item.value == num && isLoading}
            >
              {item.value}
              {type == 'sell' ? '%' : ''}
              {type == 'buy' ? ' ' + chainInfo?.symbol : ''}
            </Button>
          </HStack>
        ))}
      </Box>
    );
  };

  const maxReturnRateMemory = useMemo(() => {
    let address = message?.address || '';
    // 初始化当前地址的最大回报率
    if (!maxReturnMap.current.has(address)) {
      maxReturnMap.current.set(address, message.mktReturn || 0);
    }
    if (address && wsSimpleToken[address]) {
      // 如果没有实时价格数据，直接返回历史最大值
      if (!message.signalTokenPrice || !wsSimpleToken[address]?.token_price) {
        return maxReturnMap.current.get(message?.address || '');
      }
      // 计算当前回报率
      const currentReturn =
        +wsSimpleToken[address].token_price / +message.signalTokenPrice;
      // 更新历史最大值
      const currentMax = maxReturnMap.current.get(address) || 0;
      if (currentReturn > currentMax) {
        maxReturnMap.current.set(address, currentReturn);
      }
      return maxReturnMap.current.get(address);
    } else {
      return maxReturnMap.current.get(message?.address || '');
    }
  }, [message.address, message.signalTokenPrice, wsSimpleToken]);

  const copyAddress = async (address: string) => {
    await navigator.clipboard.writeText(address ?? '');
    toaster.create({
      title: e('addressCopied'),
      type: 'success',
    });
  };

  const onShowToken = (data: AgentData) => {
    let { address, pairAddress } = data;
    let url = `/${locale}/trade/${CHAIN_ID_TO_LOWER_NAME[chainId!]}/${address}`;
    if (pairAddress) {
      url = `${url}?pairAddress=${pairAddress}`;
    }
    router.push(url);
    nProgress.start();
  };

  const getTextColor = (
    isZero: boolean,
    isRise: boolean,
    colors?: { riseColor: string; fallColor: string; zeroColor: string },
  ) => {
    const { riseColor, fallColor, zeroColor } = colors ?? {
      riseColor: 'text-success',
      fallColor: 'text-error',
      zeroColor: 'text-success',
    };
    if (isZero) {
      return zeroColor;
    }
    return isRise ? riseColor : fallColor;
  };

  let price, mktCap, vol24h, holderCount;
  if (address && wsSimpleToken[address]) {
    price = wsSimpleToken[address]?.token_price ?? message.tokenPrice;
    mktCap = wsSimpleToken[address]?.mkt_cap ?? message.mktCap;
    vol24h = wsSimpleToken[address]?.vol_24h ?? message.vol24h;
    holderCount =
      wsSimpleToken[address]?.holder_count > 0
        ? wsSimpleToken[address]?.holder_count
        : message.holdCount;
  } else {
    price = message.tokenPrice;
    mktCap = message.mktCap;
    vol24h = message.vol24h;
    holderCount = message.holdCount;
  }

  useEffect(() => {
    if (new Decimal(price!).gte(message.signalTokenPrice ?? 0)) {
      setFadeOutColor('rgba(18, 208, 162, 0.24)');
    } else {
      setFadeOutColor('rgba(254, 91, 121, 0.24)');
    }
    controls.start({
      opacity: [1, 0],
      transition: {
        duration: 1,
      },
    });
  }, [controls, price, message.signalTokenPrice]);

  return (
    <HStack
      flexDirection={'column'}
      padding={'12px'}
      gap={'8px'}
      borderRadius={'12px'}
      border={'1px solid rgba(255, 255, 255, 0.1)'}
      className="agent-item"
      ref={ref}
    >
      <HStack
        flexDirection={'row'}
        alignItems={'center'}
        position={'relative'}
        w={'100%'}
      >
        <HStack
          position={'absolute'}
          right={0}
          top={0}
          gap={0}
          flexDirection={'row'}
          alignItems={'center'}
          zIndex={9}
        >
          <HStack
            padding={'0 21px 0 7px'}
            h={'28px'}
            background={
              'linear-gradient(to bottom right,  rgba(171, 234, 69, 0.2) 0%, rgba(18, 208, 162, 0.2) 100%)'
            }
            borderRadius={'10px 0 0 0'}
            marginRight={'-16px'}
          >
            <Text fontSize={'12px'}>{t('agent.max_returns')}</Text>
          </HStack>
          <HStack
            background={'linear-gradient(110deg,  #ABEA45 0%, #12D0A2 100%)'}
            pl={'5px'}
            pr={'5px'}
            height={'32px'}
            borderRadius={'10px 0 10px 0'}
            alignItems={'center'}
            justifyContent={'center'}
            flexDirection={'row'}
            zIndex={8}
          >
            {maxReturnRateMemory && (
              <Text color={'#000000'} fontSize={'14px'} fontWeight={600}>
                {maxReturnRateMemory < 1
                  ? '<1'
                  : maxReturnRateMemory <= 2
                    ? `${Math.ceil((maxReturnRateMemory - 1) * 100)}%`
                    : `${Number(maxReturnRateMemory).toFixed(1)}x`}
              </Text>
            )}
          </HStack>
        </HStack>
      </HStack>
      <HStack flexDirection={'column'} w={'100%'}>
        <Box
          w={'100%'}
          onMouseEnter={() => {
            router.prefetch(`/${locale}/trade/solana/${address}`);
          }}
        >
          <HStack flexDirection={'row'} className="flex-row items-center gap-1">
            <TokenAvatar
              tokenAvatar={message.tokenIcon ?? ''}
              chainAvatar={
                CHAIN_INFO[CHAIN_ID_MAP[message.chainId ?? '']]?.icon ?? ''
              }
            />
            <HStack
              flexDirection={'column'}
              alignItems={'flex-start'}
              justifyContent={'center'}
              gap={'2px'}
            >
              <Text
                className="text-foreground text-[16px] font-medium underline"
                lineHeight={'20px'}
                fontSize={'16px'}
                fontWeight={500}
                onClick={() => {
                  onShowToken(message);
                }}
                cursor={'pointer'}
              >
                {tokenInfo.symbol}
              </Text>
              <HStack
                className="flex-row items-center gap-1"
                w={'100%'}
                alignItems={'center'}
              >
                <Text fontSize={'12px'} color={'text.primary'}>
                  {(() => {
                    const date = new Date(Number(message.createTime) * 1000);
                    const today = new Date();
                    const isToday =
                      date.toDateString() === today.toDateString();

                    if (isToday) {
                      return date.toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      });
                    }
                    return date
                      .toLocaleDateString('en-US', {
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })
                      .replace(/(\d+)\/(\d+),\s(\d+):(\d+)/, '$1/$2 $3:$4');
                  })()}
                </Text>
                <Text
                  fontSize={'12px'}
                  cursor={'pointer'}
                  onClick={() => {
                    copyAddress(message.address || '');
                  }}
                >
                  {abbreviateHashString(message.address || '')}&nbsp;
                </Text>
                <Box
                  cursor={'pointer'}
                  onClick={() => {
                    copyAddress(message.address || '');
                  }}
                >
                  <Copy />
                </Box>
              </HStack>
            </HStack>
          </HStack>
        </Box>
        <Box w={'100%'} mb={'8px'}>
          {narrative ? (
            <Box p={'8px'} mb={'8px'}>
              <HStack
                fontSize={'12px'}
                gap={0}
                flexWrap={'wrap'}
                flexDirection={'row'}
                position={'relative'}
                h={narrativeExpanded ? 'auto' : '32px'}
                overflow={'hidden'}
              >
                <Text lineHeight={'16px'}>
                  <Text as="span" color={'text.primary'}>
                    {t('ai.AINarrative')}
                  </Text>
                  {narrativeExpanded
                    ? narrative
                    : `${narrative.substring(0, 110)}...`}
                </Text>
                <HStack
                  position={'absolute'}
                  bottom={'0'}
                  right={'0'}
                  gap={'2px'}
                  h={'16px'}
                  cursor={'pointer'}
                  onClick={(e) => {
                    e.stopPropagation();
                    setNarrativeExpanded(!narrativeExpanded);
                  }}
                >
                  <Text color={'text.primary'} fontSize={'12px'}>
                    {narrativeExpanded ? t('ai.Less') : t('ai.More')}
                  </Text>
                  <Image
                    src={'/images/arrow.png'}
                    alt="PumpX App Preview"
                    w={'14px'}
                    h={'14px'}
                    style={{
                      transform: narrativeExpanded
                        ? `rotate(180deg)`
                        : `rotate(0)`,
                    }}
                  />
                </HStack>
              </HStack>
            </Box>
          ) : (
            <HStack p={'8px'} fontSize={'12px'} gap={0} flexWrap={'wrap'}>
              <Text color={'text.primary'}>{t('ai.AINarrative')}</Text>
              <Text>{t('ai.thinking')}</Text>
              <Spinner color={'text.t2'} />
            </HStack>
          )}

          <HStack
            w={'100%'}
            gap={'8px'}
            pb={'6px'}
            borderBottom={'1px dashed'}
            borderBottomColor={'text.t4'}
          >
            <Box
              w={'60px'}
              h={'30px'}
              borderRight={'1px dashed'}
              borderRightColor={'text.t4'}
            >
              <Box className="flex-col gap-[2px]">
                <Text fontSize={'11px'} lineHeight={'30px'}>
                  {t('agent.signal')}
                </Text>
              </Box>
              <Box className="border border-dashed border-foreground/10 h-12" />
            </Box>
            <HStack gap={'4px'} flex={1}>
              <HStack
                gap={0}
                flex={1}
                alignItems={'center'}
                flexDirection={'column'}
              >
                <Text
                  className="gray"
                  h={'14px'}
                  lineHeight={'14px'}
                  mb={'2px'}
                  fontSize={'11px'}
                >
                  {t('agent.price')}
                </Text>
                <Text fontSize={'11px'} h={'14px'} lineHeight={'14px'}>
                  ${formatPrice(Number(message.signalTokenPrice))}
                </Text>
              </HStack>
              <HStack
                gap={0}
                flex={1}
                alignItems={'center'}
                flexDirection={'column'}
              >
                <Text
                  className="gray"
                  fontSize={'11px'}
                  h={'14px'}
                  lineHeight={'14px'}
                  mb={'2px'}
                >
                  {t('agent.mkt_cap')}
                </Text>
                <Text fontSize={'11px'} h={'14px'} lineHeight={'14px'}>
                  {message.signalMktCap
                    ? `${marketCapValueFormat(message.signalMktCap)}`
                    : '-'}
                </Text>
              </HStack>
              <HStack
                gap={0}
                flex={1}
                alignItems={'center'}
                flexDirection={'column'}
              >
                <Text
                  className="gray"
                  fontSize={'11px'}
                  h={'14px'}
                  lineHeight={'14px'}
                  mb={'2px'}
                >
                  {t('agent.vol24h')}
                </Text>
                <Text fontSize={'11px'} h={'14px'} lineHeight={'14px'}>
                  {message.signalVol24h
                    ? `${marketCapValueFormat(message.signalVol24h)}`
                    : '-'}
                </Text>
              </HStack>
              <HStack
                gap={0}
                flex={1}
                alignItems={'center'}
                flexDirection={'column'}
              >
                <Text
                  className="gray"
                  fontSize={'11px'}
                  h={'14px'}
                  lineHeight={'14px'}
                  mb={'2px'}
                >
                  {t('agent.holders')}
                </Text>
                <Text fontSize={'11px'} h={'14px'} lineHeight={'14px'}>
                  {message.signalHoldCount}
                </Text>
              </HStack>
            </HStack>
          </HStack>
          <HStack gap={'8px'} w={'100%'} pt={'6px'}>
            <Box
              w={'60px'}
              h={'30px'}
              borderRight={'1px dashed'}
              borderRightColor={'text.t4'}
            >
              <Box className="flex-col gap-[2px]">
                <Text fontSize={'11px'} lineHeight={'30px'}>
                  {t('agent.current')}
                </Text>
              </Box>
              <Box className="border border-dashed border-foreground/10 h-12" />
            </Box>
            <HStack gap={'4px'} flex={1} pos="relative">
              <motion.div
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: `linear-gradient(to right, rgba(18, 208, 162, 0), ${fadeOutColor})`,
                }}
                initial={{ opacity: 0 }}
                animate={controls}
              />
              <HStack
                gap={0}
                flex={1}
                alignItems={'center'}
                flexDirection={'column'}
              >
                <Text
                  className="gray"
                  fontSize={'11px'}
                  h={'14px'}
                  lineHeight={'14px'}
                  mb={'2px'}
                >
                  {t('agent.price')}
                </Text>
                <Text
                  color={getTextColor(
                    +Number(price) === +Number(message.signalTokenPrice),
                    !message.signalTokenPrice ||
                      +Number(price) > +message.signalTokenPrice,
                    {
                      riseColor: '#01BC8D',
                      fallColor: '#FE5B79',
                      zeroColor: 'text-foreground',
                    },
                  )}
                  fontSize={'11px'}
                  h={'14px'}
                  lineHeight={'14px'}
                >
                  ${formatPrice(Number(price))}
                </Text>
              </HStack>
              <HStack
                gap={0}
                flex={1}
                alignItems={'center'}
                flexDirection={'column'}
              >
                <Text
                  className="gray"
                  fontSize={'11px'}
                  h={'14px'}
                  lineHeight={'14px'}
                  mb={'2px'}
                >
                  {t('agent.mkt_cap')}
                </Text>
                <Text
                  color={getTextColor(
                    +Number(mktCap) === +Number(message.signalMktCap),
                    !message.signalMktCap ||
                      +Number(mktCap) > +message.signalMktCap,
                    {
                      riseColor: '#01BC8D',
                      fallColor: '#FE5B79',
                      zeroColor: 'text-foreground',
                    },
                  )}
                  fontSize={'11px'}
                  h={'14px'}
                  lineHeight={'14px'}
                >
                  {mktCap ? `$${formatNumber(Number(mktCap))}` : '-'}
                </Text>
              </HStack>
              <HStack
                gap={0}
                flex={1}
                alignItems={'center'}
                flexDirection={'column'}
              >
                <Text
                  className="gray"
                  fontSize={'11px'}
                  h={'14px'}
                  lineHeight={'14px'}
                  mb={'2px'}
                >
                  {t('agent.vol24h')}
                </Text>
                <Text
                  color={getTextColor(
                    +Number(vol24h) === +Number(message.signalVol24h),
                    !message.signalVol24h ||
                      +Number(vol24h) > +message.signalVol24h,
                    {
                      riseColor: '#01BC8D',
                      fallColor: '#FE5B79',
                      zeroColor: 'text-foreground',
                    },
                  )}
                  fontSize={'11px'}
                  h={'14px'}
                  lineHeight={'14px'}
                >
                  {vol24h ? `$${formatNumber(Number(vol24h))}` : '-'}
                </Text>
              </HStack>
              <HStack
                gap={0}
                flex={1}
                alignItems={'center'}
                flexDirection={'column'}
              >
                <Text
                  className="gray"
                  fontSize={'11px'}
                  h={'14px'}
                  lineHeight={'14px'}
                  mb={'2px'}
                >
                  {t('agent.holders')}
                </Text>
                <Text
                  color={getTextColor(
                    +Number(holderCount) === +Number(message.signalHoldCount),
                    !message.signalHoldCount ||
                      +Number(holderCount) > +message.signalHoldCount,
                    {
                      riseColor: '#01BC8D',
                      fallColor: '#FE5B79',
                      zeroColor: 'text-foreground',
                    },
                  )}
                  fontSize={'11px'}
                  h={'14px'}
                  lineHeight={'14px'}
                >
                  {holderCount}
                </Text>
              </HStack>
            </HStack>
          </HStack>
        </Box>
        {renderQuickOneClickBuy(quickBuyInfo, 'buy')}
        {/* {renderQuickOneClickBuy(['25', '50', '75', '100'], 'sell')} */}
      </HStack>
    </HStack>
  );
}
